import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import './styles.scss';

const CornerSpinner = ({
  isLoading = false,
  delay = 150,
  minVisibleMs = 400,
  exitDuration = 220,
  size = 'md', // 'sm' | 'md' | 'lg'
  label = 'Loading…',
  className,
}) => {
  const [isMounted, setIsMounted] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [visibleAt, setVisibleAt] = useState(null);

  useEffect(() => {
    let showTimer;
    let startExitTimer;
    let unmountTimer;

    if (isLoading) {
      // Cancel any pending exit/unmount
      if (startExitTimer) clearTimeout(startExitTimer);
      if (unmountTimer) clearTimeout(unmountTimer);
      setIsExiting(false);
      if (!isMounted) setIsMounted(true);
      showTimer = setTimeout(
        () => {
          setIsVisible(true);
          setVisibleAt(Date.now());
        },
        Math.max(0, delay)
      );
    } else {
      // If not mounted or not yet visible, just schedule unmount after delay guarding
      const elapsed = visibleAt ? Date.now() - visibleAt : 0;
      const remainingMin = Math.max(0, minVisibleMs - elapsed);
      startExitTimer = setTimeout(() => {
        setIsExiting(true);
        unmountTimer = setTimeout(() => {
          setIsVisible(false);
          setIsExiting(false);
          setIsMounted(false);
          setVisibleAt(null);
        }, exitDuration);
      }, remainingMin);
    }

    return () => {
      if (showTimer) clearTimeout(showTimer);
      if (startExitTimer) clearTimeout(startExitTimer);
      if (unmountTimer) clearTimeout(unmountTimer);
    };
  }, [isLoading, delay, minVisibleMs, exitDuration, isMounted, visibleAt]);

  if (!isMounted) return null;

  const classes = [
    'corner-spinner',
    `corner-spinner--${size}`,
    isExiting && 'corner-spinner--exiting',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={classes}
      role="status"
      aria-live="polite"
      aria-label={label}
      aria-busy="true"
      hidden={!isVisible && !isExiting}
    >
      <div className="corner-spinner__spinner" />
      <span className="corner-spinner__sr-only">{label}</span>
    </div>
  );
};

export default CornerSpinner;

CornerSpinner.propTypes = {
  isLoading: PropTypes.bool,
  delay: PropTypes.number,
  minVisibleMs: PropTypes.number,
  exitDuration: PropTypes.number,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  label: PropTypes.string,
  className: PropTypes.string,
};

CornerSpinner.defaultProps = {
  isLoading: false,
  delay: 150,
  minVisibleMs: 400,
  exitDuration: 220,
  size: 'md',
  label: 'Loading…',
  className: undefined,
};
